import { Box } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Component, ErrorInfo, ReactNode, memo } from 'react';
import { CONFIG } from 'shared/config';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

// Define the static property in the class definition
export class ErrorBoundary extends Component<Props, State> {
  // Add static property to the class
  static displayName = 'ErrorBoundary';

  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  // Centralized error logging function for consistent logging in both environments
  private logError(error: Error, errorInfo: ErrorInfo): void {
    console.error('=== Error Boundary Caught Error ===');
    console.error('Error message:', error.message);
    console.error('Error name:', error.name);
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Use the centralized logging function for both environments
    this.logError(error, errorInfo);

    // Store the full error info in state
    this.setState({ errorInfo });

    // Call the onError callback if provided
    this.props.onError?.(error, errorInfo);
  }

  public render() {
    if (this.state.hasError && !CONFIG.isProduction) {
      return (
        this.props.fallback || (
          <Box sx={{ width: '100%', height: '100%', padding: '20px', overflow: 'auto' }}>
            <Alert severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
              <p>
                <strong>Error:</strong> {this.state.error?.name}: {this.state.error?.message}
              </p>
              {/* Display detailed component stack trace */}
              {this.state.errorInfo && (
                <>
                  <p>
                    <strong>Component Stack:</strong>
                  </p>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflow: 'auto',
                      maxHeight: '300px',
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {this.state.errorInfo.componentStack}
                  </pre>
                </>
              )}
              {/* Display error stack trace */}
              {this.state.error?.stack && (
                <>
                  <p>
                    <strong>Error Stack:</strong>
                  </p>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      overflow: 'auto',
                      maxHeight: '300px',
                      backgroundColor: '#f5f5f5',
                      padding: '10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                    }}
                  >
                    {this.state.error.stack}
                  </pre>
                </>
              )}
            </Alert>
          </Box>
        )
      );
    }

    // If no error, render children as normal
    return this.props.children;
  }
}

// Create memoized version
export const MemoizedErrorBoundary = memo(ErrorBoundary);
MemoizedErrorBoundary.displayName = 'MemoizedErrorBoundary';
